import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { timeTunnelService, HistoricalEvent } from '../../services/timeTunnelService';
import PageHeader from '../common/PageHeader';
import EventForm from './EventForm';
import AIGenerateForm from './AIGenerateForm';
import EventList from './EventList';
import YearTimeline from './YearTimeline';
import LocationComparison from './LocationComparison';
import './TimeTunnel.css';

interface TimeTunnelProps {
  onToggleSidebar: () => void;
}

const TimeTunnel: React.FC<TimeTunnelProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<HistoricalEvent[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Selected filters
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  
  // UI state
  const [showEventForm, setShowEventForm] = useState(false);
  const [showAIGenerateForm, setShowAIGenerateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<HistoricalEvent | null>(null);
  const [currentYearRange, setCurrentYearRange] = useState({ start: 1900, end: 2100 });
  const [viewMode, setViewMode] = useState<'timeline' | 'location'>('timeline');
  const [showFilters, setShowFilters] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchInitialData();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [user, selectedYear, selectedMonth, selectedDay, selectedCountry]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [yearsResponse, countriesResponse] = await Promise.all([
        timeTunnelService.getYears(),
        timeTunnelService.getCountries()
      ]);

      if (yearsResponse.success) {
        setYears(yearsResponse.years);
        if (yearsResponse.years.length > 0) {
          const minYear = Math.min(...yearsResponse.years);
          const maxYear = Math.max(...yearsResponse.years);
          setCurrentYearRange({ start: minYear, end: maxYear });
          setSelectedYear(yearsResponse.years[0]); // Select first year by default
        }
      }

      if (countriesResponse.success) {
        setCountries(countriesResponse.countries);
      }
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    if (!user) return;

    try {
      const params: any = {};
      if (selectedYear !== null) params.year = selectedYear;
      if (selectedMonth !== null) params.month = selectedMonth;
      if (selectedDay !== null) params.day = selectedDay;
      if (selectedCountry !== null) params.country = selectedCountry;
      params.limit = 1000;

      const response = await timeTunnelService.getEvents(params);
      if (response.success) {
        setEvents(response.events);
      } else {
        setError('Failed to load events');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
    }
  };

  const handleYearClick = (year: number) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
  };

  const handleMonthChange = (month: number | null) => {
    setSelectedMonth(month);
    setSelectedDay(null);
  };

  const handleDayChange = (day: number | null) => {
    setSelectedDay(day);
  };

  const handleCountryChange = (country: string | null) => {
    setSelectedCountry(country);
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setShowEventForm(true);
  };

  const handleAIGenerateClick = () => {
    setShowAIGenerateForm(true);
  };

  const handleAIGenerateSaved = async () => {
    setShowAIGenerateForm(false);
    await fetchEvents();
    await fetchInitialData(); // Refresh years and countries
  };

  const handleEditEvent = (event: HistoricalEvent) => {
    setEditingEvent(event);
    setShowEventForm(true);
  };

  const handleDeleteEvent = async (eventId: number) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      const response = await timeTunnelService.deleteEvent(eventId);
      if (response.success) {
        await fetchEvents();
        await fetchInitialData(); // Refresh years and countries
      } else {
        alert('Failed to delete event');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event');
    }
  };

  const handleEventSaved = async () => {
    setShowEventForm(false);
    setEditingEvent(null);
    await fetchEvents();
    await fetchInitialData(); // Refresh years and countries
  };

  const handleYearRangeChange = (start: number, end: number) => {
    setCurrentYearRange({ start, end });
  };

  const handleEventClick = (event: HistoricalEvent) => {
    setEditingEvent(event);
    setShowEventForm(true);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedMonth !== null) count++;
    if (selectedCountry !== null) count++;
    return count;
  };

  const rightActions = (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        className="time-tunnel-menu-btn"
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
      >
        ⋯
      </button>
      {showMenu && (
        <div className="time-tunnel-menu-dropdown">
          <button
            className={`time-tunnel-menu-item ${viewMode === 'timeline' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('timeline');
              setShowMenu(false);
            }}
          >
            View: Timeline
          </button>
          <button
            className={`time-tunnel-menu-item ${viewMode === 'location' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('location');
              setShowMenu(false);
            }}
          >
            View: Location
          </button>
          <button
            className={`time-tunnel-menu-item ${showFilters ? 'active' : ''}`}
            onClick={() => {
              setShowFilters(!showFilters);
              setShowMenu(false);
            }}
          >
            Filters{getActiveFiltersCount() > 0 ? ` (${getActiveFiltersCount()})` : ''}
          </button>
          <button
            className="time-tunnel-menu-item"
            onClick={() => {
              handleAddEvent();
              setShowMenu(false);
            }}
          >
            + Add Event
          </button>
          <button
            className="time-tunnel-menu-item"
            onClick={() => {
              handleAIGenerateClick();
              setShowMenu(false);
            }}
          >
            AI Generate Events
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="time-tunnel-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="time-tunnel-header-title">/time-tunnel</h1>}
        rightActions={rightActions}
      />
      
      <div className="time-tunnel-content">
        {loading && <div className="time-tunnel-loading">Loading...</div>}
        {error && <div className="time-tunnel-error">{error}</div>}

        {!loading && (
          <>
            {showFilters && (
              <div className="time-tunnel-filters">
                <div className="time-tunnel-filter-row">
                  <div className="time-tunnel-filter-group">
                    <label>Month:</label>
                    <select
                      value={selectedMonth || ''}
                      onChange={(e) => handleMonthChange(e.target.value ? parseInt(e.target.value) : null)}
                      className="time-tunnel-select"
                    >
                      <option value="">All</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <option key={month} value={month}>
                          {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="time-tunnel-filter-group">
                    <label>Country:</label>
                    <select
                      value={selectedCountry || ''}
                      onChange={(e) => handleCountryChange(e.target.value || null)}
                      className="time-tunnel-select"
                    >
                      <option value="">All</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedYear(null);
                      setSelectedMonth(null);
                      setSelectedDay(null);
                      setSelectedCountry(null);
                    }}
                    className="time-tunnel-clear-btn"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {viewMode === 'timeline' ? (
              <>
                <YearTimeline
                  years={years}
                  events={events}
                  selectedYear={selectedYear}
                  currentRange={currentYearRange}
                  onYearClick={handleYearClick}
                  onRangeChange={handleYearRangeChange}
                />

                <EventList
                  events={events}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  selectedDay={selectedDay}
                  selectedCountry={selectedCountry}
                  onEdit={handleEditEvent}
                  onDelete={handleDeleteEvent}
                />
              </>
            ) : (
              <LocationComparison
                events={events}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selectedDay={selectedDay}
                onEventClick={handleEventClick}
              />
            )}
          </>
        )}
      </div>

      {showEventForm && (
        <EventForm
          event={editingEvent}
          countries={countries}
          onSave={handleEventSaved}
          onCancel={() => {
            setShowEventForm(false);
            setEditingEvent(null);
          }}
        />
      )}

      {showAIGenerateForm && (
        <AIGenerateForm
          countries={countries}
          onSave={handleAIGenerateSaved}
          onCancel={() => {
            setShowAIGenerateForm(false);
          }}
        />
      )}
    </div>
  );
};

export default TimeTunnel;

